import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLocation } from 'react-router-dom';


const Checkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const buyNowItemId = location.state?.buyNowItemId;
    const [addresses, setAddresses] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [useNewAddress, setUseNewAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cartTotal, setCartTotal] = useState(0);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [confirmationText, setConfirmationText] = useState('');

    const [orderData, setOrderData] = useState(null);

    const [formData, setFormData] = useState({
        addressLabel: '', fullName: '', company: '', streetAddress: '',
        city: '', province: '', postalCode: '', phone: '', email: '', deliveryNotes: ''
    });

    const itemsToDisplay = buyNowItemId
        ? cartItems.filter(item => item.cart_item_id === buyNowItemId)
        : cartItems;
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const subtotal = itemsToDisplay.reduce(
        (sum, item) => sum + (parseFloat(item.prices?.total_price || 0) * item.quantity),
        0
    );
    const tax = parseFloat((subtotal * 0.13).toFixed(2));
    const total = parseFloat((subtotal + tax).toFixed(2));

    const canadianProvinces = [
        { value: '', label: 'Select Province' },
        { value: 'AB', label: 'Alberta' },
        { value: 'BC', label: 'British Columbia' },
        { value: 'MB', label: 'Manitoba' },
        { value: 'NB', label: 'New Brunswick' },
        { value: 'NL', label: 'Newfoundland and Labrador' },
        { value: 'NS', label: 'Nova Scotia' },
        { value: 'NT', label: 'Northwest Territories' },
        { value: 'NU', label: 'Nunavut' },
        { value: 'ON', label: 'Ontario' },
        { value: 'PE', label: 'Prince Edward Island' },
        { value: 'QC', label: 'Quebec' },
        { value: 'SK', label: 'Saskatchewan' },
        { value: 'YT', label: 'Yukon' }
    ];


    useEffect(() => {
        checkAuthenticationAndLoadData();
    }, []);


    const checkAuthenticationAndLoadData = async () => {
        setPageLoading(true);
        const token = localStorage.getItem('access_token');

        if (!token) {
            setIsAuthenticated(false);
            setPageLoading(false);
            return;
        }

        setIsAuthenticated(true);

        try {
            await Promise.all([
                loadAddresses(token),
                loadCartItems(token)
            ]);
        } catch (error) {
            console.error('Error loading checkout data:', error);
        } finally {
            setPageLoading(false);
        }
    };


    const loadAddresses = async (token) => {
        if (!token) return;
        try {
            const response = await axios.get(`${API_BASE_URL}/addresses/`,
                { headers: { 'Authorization': `Bearer ${token}` } });
            setAddresses(response.data);
            if (response.data.length > 0) {
                setSelectedAddressId(response.data[0].id);
                setUseNewAddress(false);
            } else {
                setUseNewAddress(true);
            }
        } catch (error) {
            console.error('Error loading addresses:', error);
            if (error.response?.status === 401) {
                handleTokenExpired();
            }
            setAddresses([]);
            setUseNewAddress(true);
        }
    };


    const loadCartItems = async (token, buyNowItemId = null) => {
        if (!token) return;
        try {
            const response = await axios.get(`${API_BASE_URL}/cart/my-cart/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            let items = response.data.items || [];
            if (buyNowItemId) {
                // Filter to only the Buy Now item if provided
                items = items.filter(item => item.cart_item_id === buyNowItemId);
            }

            setCartItems(items);
            setCartTotal(items.reduce((sum, item) => sum + (parseFloat(item.prices?.total_price || 0) * item.quantity), 0));
        } catch (error) {
            console.error('Error loading cart:', error);
            if (error.response?.status === 401) {
                handleTokenExpired();
            }
            setCartItems([]);
        }
    };



    const handleTokenExpired = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setIsAuthenticated(false);
        setModalMessage('Your session has expired. Please login again.');
        setShowSuccessModal(true);
        setTimeout(() => {
            setShowSuccessModal(false);
            navigate('/account');
        }, 2500);
    };


    const handleLogin = () => {
        navigate('/account');
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };


    const handleAddressSelect = (addressId) => {
        setSelectedAddressId(addressId);
        setUseNewAddress(false);
    };


    const toggleNewAddressForm = () => {
        setUseNewAddress(!useNewAddress);
    };


    const handleSaveAddress = async () => {
        const { addressLabel, fullName, streetAddress, city, province, postalCode, phone, email } = formData;

        // ✅ Enhanced validation
        if (!addressLabel || !fullName || !streetAddress || !city || !province || !postalCode || !phone || !email) {
            alert('Please fill all required fields');
            return;
        }

        // ✅ Validate postal code format (Canadian)
        const postalCodeRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
        if (!postalCodeRegex.test(postalCode)) {
            alert('Please enter a valid Canadian postal code (e.g., A1A 1A1)');
            return;
        }

        // ✅ Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address');
            return;
        }

        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');
            const payload = {
                address_data: { ...formData },
                is_default: addresses.length === 0
            };
            const response = await axios.post(`${API_BASE_URL}/addresses/`, payload,
                { headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' } });

            setAddresses([...addresses, response.data]);
            setSelectedAddressId(response.data.id);
            setUseNewAddress(false);
            setModalMessage(`Address "${formData.addressLabel}" saved successfully!`);
            setShowSuccessModal(true);
            setFormData({
                addressLabel: '', fullName: '', company: '', streetAddress: '',
                city: '', province: '', postalCode: '', phone: '', email: '', deliveryNotes: ''
            });
            setTimeout(() => setShowSuccessModal(false), 2000);
        } catch (error) {
            console.error('Error saving address:', error);
            if (error.response?.status === 401) {
                handleTokenExpired();
            } else {
                alert(error.response?.data?.detail || 'Failed to save address. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };


    const handleDeleteAddress = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');
            await axios.delete(`${API_BASE_URL}/addresses/${addressId}/`,
                { headers: { 'Authorization': `Bearer ${accessToken}` } });

            const updated = addresses.filter(addr => addr.id !== addressId);
            setAddresses(updated);

            if (selectedAddressId === addressId) {
                setSelectedAddressId(updated.length > 0 ? updated[0].id : null);
            }

            if (updated.length === 0) setUseNewAddress(true);

            setModalMessage('Address deleted successfully!');
            setShowSuccessModal(true);
            setTimeout(() => setShowSuccessModal(false), 1500);
        } catch (error) {
            console.error('Error deleting address:', error);
            if (error.response?.status === 401) {
                handleTokenExpired();
            } else {
                alert('Failed to delete address. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };


    const handlePlaceOrder = (e) => {
        e.preventDefault();

        // ✅ Enhanced validation checks
        if (!isAuthenticated) {
            alert('Please login to place an order');
            navigate('/account');
            return;
        }

        if (itemsToDisplay.length === 0) {
            alert('Your cart is empty. Please add items before placing an order.');
            return;
        }

        if (useNewAddress) {
            alert('Please save the address before placing an order.');
            return;
        }

        const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
        if (!selectedAddress) {
            alert('Please select a delivery address.');
            return;
        }

        // ✅ Validate cart items have valid prices
        const invalidItems = itemsToDisplay.filter(item => !item.prices?.total_price || parseFloat(item.prices.total_price) <= 0);
        if (invalidItems.length > 0) {
            alert('Some items in your cart are missing price information. Please refresh your cart or contact support.');
            console.error('Invalid cart items:', invalidItems);
            return;
        }




        // ✅ Validate calculations
        if (isNaN(subtotal) || isNaN(tax) || isNaN(total) || subtotal <= 0) {
            alert('There was an error calculating your order total. Please refresh and try again.');
            return;
        }

        const order = {
            address_id: selectedAddress.id,
            items: itemsToDisplay.map(item => ({
                material_id: item.material.id,
                product_type_id: item.product_type.id,
                quantity: item.quantity,
                customizations: item.customizations || {},
                prices: item.prices || {},
                customization_image_id: item.customization_image_id || null
            })),
            subtotal: subtotal,
            tax: tax,
            shipping: 0,
            discount_amount: 0,
            total_amount: total
        };


        setOrderData(order);
        setShowConfirmModal(true);
        setConfirmationText('');
    };


    const handleConfirmOrder = async () => {
        if (confirmationText.toLowerCase() !== 'confirm') {
            alert('Please type "confirm" to proceed with your order');
            return;
        }

        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');

            // ✅ Make order creation request
            const response = await axios.post(`${API_BASE_URL}/orders/`, orderData,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

            // ✅ Clear cart after successful order
            await deleteCartItems();

            // ✅ Reset modal states
            setShowConfirmModal(false);
            setOrderData(null);
            setConfirmationText('');

            // ✅ Show success message
            setModalMessage(`Order #${response.data.order_number} placed successfully!`);
            setShowSuccessModal(true);

            // ✅ Navigate to order details
            setTimeout(() => {
                setShowSuccessModal(false);
                navigate(`/`);
            }, 2000);

        } catch (error) {
            console.error('Error placing order:', error);

            if (error.response?.status === 401) {
                handleTokenExpired();
            } else {
                // ✅ Improved error handling
                const errorMessage = error.response?.data?.detail
                    || error.response?.data?.message
                    || 'Failed to place order. Please try again.';

                alert(errorMessage);

                // ✅ Close confirmation modal on error
                setShowConfirmModal(false);
                setConfirmationText('');
            }
        } finally {
            setLoading(false);
        }
    };


    const deleteCartItems = async () => {
        try {
            const accessToken = localStorage.getItem('access_token');

            // ✅ Delete all cart items
            const deletePromises = itemsToDisplay.map(item =>
                axios.delete(`${API_BASE_URL}/cart/remove-item/${item.cart_item_id}/`,
                    { headers: { 'Authorization': `Bearer ${accessToken}` } })
            );

            await Promise.all(deletePromises);

            // ✅ Clear local state
            setCartItems([]);
            setCartTotal(0);
            localStorage.removeItem('cart');

        } catch (error) {
            console.error('Error clearing cart:', error);
            // ✅ Don't block order completion if cart deletion fails
        }
    };


    if (pageLoading) {
        return (
            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: '50px',
                                        height: '50px',
                                        border: '4px solid #f0f0f0',
                                        borderTop: '4px solid #3b82f6',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto 20px'
                                    }}></div>
                                    <p className="text-gray-600">Loading checkout...</p>
                                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }


    if (!isAuthenticated) {
        return (
            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="border border-gray-100 rounded-8 p-40 text-center">
                                <div style={{ marginBottom: '30px' }}>
                                    <i className="ph ph-lock" style={{ fontSize: '60px', color: '#3b82f6' }}></i>
                                </div>
                                <h5 className="mb-24">Authentication Required</h5>
                                <p className="text-gray-600 mb-32">
                                    You need to be logged in to place an order. Please login or create an account.
                                </p>
                                <div className="d-flex gap-3 justify-content-center">
                                    <button onClick={handleLogin} className="btn btn-main px-40">
                                        <i className="ph ph-sign-in me-2"></i>
                                        Login
                                    </button>
                                    <Link to="/account" className="btn btn-outline-main px-40">
                                        <i className="ph ph-user-plus me-2"></i>
                                        Create Account
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }


    return (
        <>
            {showConfirmModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '40px', borderRadius: '12px',
                        maxWidth: '500px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}>
                        <h5 className="mb-24">Confirm Your Order</h5>
                        <p className="text-gray-600 mb-20">Type <span className="fw-bold">"confirm"</span> to proceed</p>
                        <input
                            type="text"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && confirmationText.toLowerCase() === 'confirm' && !loading) {
                                    handleConfirmOrder();
                                }
                            }}
                            className="common-input border-gray-100 mb-24"
                            placeholder="Type 'confirm'"
                            disabled={loading}
                            autoFocus
                        />
                        <div className="d-flex gap-2 justify-content-center">
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setConfirmationText('');
                                }}
                                className="btn btn-outline-main"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmOrder}
                                className="btn btn-main"
                                disabled={loading || confirmationText.toLowerCase() !== 'confirm'}
                            >
                                {loading ? 'Processing...' : 'Confirm Order'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '40px', borderRadius: '12px',
                        maxWidth: '400px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}>
                        <div style={{
                            width: '60px', height: '60px', backgroundColor: '#10b981', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
                        }}>
                            <i className="ph ph-check" style={{ fontSize: '32px', color: 'white' }}></i>
                        </div>
                        <h5 className="mb-16">Success!</h5>
                        <p className="text-gray-600 mb-24">{modalMessage}</p>
                        <button onClick={() => setShowSuccessModal(false)} className="btn btn-main px-32">Close</button>
                    </div>
                </div>
            )}


            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="row">
                        <div className="col-xl-9 col-lg-8">
                            {addresses.length > 0 && (
                                <div className="mb-40">
                                    <h6 className="text-lg mb-24 fw-bold">Saved Addresses ({addresses.length})</h6>
                                    {!useNewAddress && (
                                        <div className="row gy-3 mb-24">
                                            {addresses.map((address) => (
                                                <div key={address.id} className="col-md-6">
                                                    <div className={`border rounded-8 p-20 position-relative ${selectedAddressId === address.id ? 'border-main-600 bg-main-50' : 'border-gray-100'}`}
                                                        style={{ cursor: 'pointer' }}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteAddress(address.id);
                                                            }}
                                                            disabled={loading}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '10px',
                                                                right: '10px',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                color: '#dc2626',
                                                                padding: '5px'
                                                            }}
                                                            title="Delete address"
                                                        >
                                                            <i className="ph ph-trash text-xl"></i>
                                                        </button>
                                                        <div onClick={() => handleAddressSelect(address.id)}>
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name="savedAddress"
                                                                    id={`address-${address.id}`}
                                                                    checked={selectedAddressId === address.id}
                                                                    onChange={() => handleAddressSelect(address.id)}
                                                                />
                                                                <label className="form-check-label" htmlFor={`address-${address.id}`}>
                                                                    <div className="fw-bold text-main-600 mb-1">
                                                                        <i className="ph ph-map-pin me-1"></i>
                                                                        {address.address_data.addressLabel}
                                                                    </div>
                                                                    <div className="fw-semibold">{address.address_data.fullName}</div>
                                                                    <div className="text-sm text-gray-600 mt-1">{address.address_data.streetAddress}</div>
                                                                    <div className="text-sm text-gray-600">
                                                                        {address.address_data.city}, {address.address_data.province} {address.address_data.postalCode}
                                                                    </div>
                                                                    <div className="text-sm text-gray-600">{address.address_data.phone}</div>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-outline-main"
                                        onClick={toggleNewAddressForm}
                                        disabled={loading}
                                    >
                                        <i className={`ph ${useNewAddress ? 'ph-list' : 'ph-plus'} me-2`}></i>
                                        {useNewAddress ? 'Use Saved Address' : 'Add New Address'}
                                    </button>
                                </div>
                            )}


                            {useNewAddress && (
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <h6 className="text-lg mb-24 fw-bold">
                                        {addresses.length > 0 ? 'New Address' : 'Delivery Address'}
                                    </h6>
                                    <div className="row gy-3">
                                        {Object.keys(formData).map(key => {
                                            if (key === 'province') {
                                                return (
                                                    <div key={key} className="col-md-4">
                                                        <select
                                                            name={key}
                                                            value={formData[key]}
                                                            onChange={handleInputChange}
                                                            className="common-input border-gray-100"
                                                            required
                                                            disabled={loading}
                                                        >
                                                            {canadianProvinces.map(prov => (
                                                                <option key={prov.value} value={prov.value}>
                                                                    {prov.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                );
                                            }

                                            if (key === 'deliveryNotes') {
                                                return (
                                                    <div key={key} className="col-12">
                                                        <textarea
                                                            name={key}
                                                            value={formData[key]}
                                                            onChange={handleInputChange}
                                                            className="common-input border-gray-100"
                                                            rows="2"
                                                            placeholder="Delivery Instructions (Optional)"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                );
                                            }

                                            const required = ['addressLabel', 'fullName', 'streetAddress', 'city', 'postalCode', 'phone', 'email'].includes(key);
                                            const fieldLabels = {
                                                addressLabel: 'Address Label',
                                                fullName: 'Full Name',
                                                company: 'Company',
                                                streetAddress: 'Street Address',
                                                city: 'City',
                                                postalCode: 'Postal Code',
                                                phone: 'Phone',
                                                email: 'Email'
                                            };

                                            return (
                                                <div key={key} className="col-md-4">
                                                    <input
                                                        type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                                                        name={key}
                                                        value={formData[key]}
                                                        onChange={handleInputChange}
                                                        className="common-input border-gray-100"
                                                        placeholder={`${fieldLabels[key] || key}${required ? ' *' : ''}`}
                                                        required={required}
                                                        disabled={loading}
                                                        pattern={key === 'postalCode' ? '[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]' : undefined}
                                                        title={key === 'postalCode' ? 'Please enter postal code in format: A1A 1A1' : undefined}
                                                    />
                                                </div>
                                            );
                                        })}
                                        <div className="col-12">
                                            <button
                                                type="button"
                                                onClick={handleSaveAddress}
                                                className="btn btn-outline-main"
                                                disabled={loading}
                                            >
                                                {loading ? 'Saving...' : (
                                                    <>
                                                        <i className="ph ph-floppy-disk me-2"></i>
                                                        Save Address
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>


                        <div className="col-xl-3 col-lg-4">
                            <div className="checkout-sidebar">
                                <div className="bg-color-three rounded-8 p-24 text-center mb-24">
                                    <span className="text-gray-900 text-xl fw-semibold">Your Order</span>
                                </div>
                                <div className="border border-gray-100 rounded-8 px-24 py-32">
                                    {itemsToDisplay.length > 0 ? (
                                        <>
                                            <div className="mb-32 pb-24 border-bottom border-gray-100 flex-between gap-8">
                                                <span className="text-gray-900 fw-medium text-lg">Product</span>
                                                <span className="text-gray-900 fw-medium text-lg">Subtotal</span>
                                            </div>
                                            {itemsToDisplay.map((item, index) => (
                                                <div key={item.cart_item_id || index} className="flex-between gap-12 mb-24">
                                                    <div className="flex-align gap-8">
                                                        <span className="text-gray-900 fw-normal text-sm">
                                                            {item.product_type.name}
                                                        </span>
                                                        <span className="text-gray-500">×</span>
                                                        <span className="text-gray-900 fw-semibold text-sm">
                                                            {item.quantity}
                                                        </span>
                                                    </div>
                                                    <span className="text-gray-900 fw-bold text-sm">
                                                        ${(parseFloat(item.prices?.total_price || 0) * item.quantity).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                            <div className="border-top border-gray-100 pt-24 mt-24">
                                                <div className="mb-20 flex-between gap-8">
                                                    <span className="text-gray-900 text-lg fw-semibold">Subtotal</span>
                                                    <span className="text-gray-900 text-md fw-bold">
                                                        ${subtotal.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="mb-20 flex-between gap-8">
                                                    <span className="text-gray-900 text-lg fw-semibold">Tax (13%)</span>
                                                    <span className="text-gray-900 text-md fw-bold">
                                                        ${tax.toFixed(2)}
                                                    </span>
                                                </div>
                                                <div className="flex-between gap-8 pt-20 border-top border-gray-100">
                                                    <span className="text-gray-900 text-xl fw-bold">Total</span>
                                                    <span className="text-main-600 text-xl fw-bold">
                                                        ${total.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>

                                        </>
                                    ) : (
                                        <div className="text-center py-32">
                                            <i className="ph ph-shopping-cart" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
                                            <p className="text-gray-500">Your cart is empty</p>
                                            <Link to="/products" className="btn btn-outline-main mt-3">
                                                Browse Products
                                            </Link>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-32 pt-24 border-top border-gray-100">
                                    <p className="text-gray-500 text-sm">
                                        Your data will be used to process your order. See our{" "}
                                        <Link to="/privacy" className="text-main-600 text-decoration-underline">
                                            privacy policy
                                        </Link>.
                                    </p>
                                </div>
                                <button
                                    onClick={handlePlaceOrder}
                                    className="btn btn-main py-18 w-100 rounded-8 mt-32"
                                    disabled={itemsToDisplay.length === 0 || loading || !selectedAddressId}
                                >
                                    {loading ? (
                                        'Processing...'
                                    ) : (
                                        `Place Order - $${itemsToDisplay.length > 0 ? total.toFixed(2) : '0.00'}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};


export default Checkout;
